import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Button } from "@/shared/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import type { PatchFormInput } from "../settings.patch.schemas";
import {
  reorderByDragId,
  SortableItem,
  useSortableSensors,
} from "./SortableItem";

export function PatchRaidEditor({
  raidIndex,
  raidKey,
  onRemove,
}: {
  raidIndex: number;
  raidKey: string;
  onRemove: () => void;
}) {
  const form = useFormContext<PatchFormInput>();
  const bosses = useFieldArray({
    control: form.control,
    name: `raids.${raidIndex}.bosses`,
    keyName: "fieldKey",
  });
  const sensors = useSortableSensors();
  const [activeBossId, setActiveBossId] = useState<string | null>(null);

  const onBossDragStart = ({ active }: DragStartEvent) =>
    setActiveBossId(String(active.id));
  const onBossDragEnd = (event: DragEndEvent) => {
    reorderByDragId(
      bosses.fields,
      (boss) => bossDragId(raidKey, boss.fieldKey),
      event,
      bosses.move,
    );
    setActiveBossId(null);
  };

  const activeBossIndex = bosses.fields.findIndex(
    (boss) => bossDragId(raidKey, boss.fieldKey) === activeBossId,
  );
  const activeBosses = form.watch(`raids.${raidIndex}.bosses`);
  const activeBoss =
    activeBossIndex >= 0 ? activeBosses[activeBossIndex] : null;

  const bossArrayError = form.formState.errors.raids?.[raidIndex]?.bosses;

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div>
        <FormField
          control={form.control}
          name={`raids.${raidIndex}.name`}
          render={({ field }) => (
            <FormItem className="grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2">
              <FormLabel className="col-span-2">Raid</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Ta bort raid"
                className="text-destructive"
                onClick={onRemove}
              >
                <Trash2 />
              </Button>
              <FormMessage className="col-span-2" />
            </FormItem>
          )}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium">Bossar</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => bosses.append({ id: null, name: "" })}
          >
            <Plus />
            Lägg till boss
          </Button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onBossDragStart}
          onDragEnd={onBossDragEnd}
          onDragCancel={() => setActiveBossId(null)}
        >
          <div className="space-y-2">
            {bosses.fields.map((boss, bossIndex) => (
              <SortableItem
                key={boss.fieldKey}
                id={bossDragId(raidKey, boss.fieldKey)}
                label={`Flytta boss ${bossIndex + 1}`}
                className="rounded-md border bg-background"
              >
                {(bossHandle) => (
                  <div className="flex items-center gap-1 p-2">
                    <FormField
                      control={form.control}
                      name={`raids.${raidIndex}.bosses.${bossIndex}.name`}
                      render={({ field }) => (
                        <FormItem className="min-w-0 flex-1">
                          <FormLabel className="sr-only">
                            Boss {bossIndex + 1}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={`Boss ${bossIndex + 1}`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Ta bort boss ${bossIndex + 1}`}
                      className="text-destructive"
                      onClick={() => bosses.remove(bossIndex)}
                    >
                      <Trash2 />
                    </Button>
                    {bossHandle}
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeBoss && (
              <BossDragPreview boss={activeBoss} index={activeBossIndex} />
            )}
          </DragOverlay>
        </DndContext>

        {bossArrayError?.message && (
          <p className="text-sm text-destructive">{bossArrayError.message}</p>
        )}
      </div>
    </section>
  );
}

function bossDragId(raidKey: string, bossKey: string) {
  return `boss:${raidKey}:${bossKey}`;
}

function BossDragPreview({
  boss,
  index,
}: {
  boss: PatchFormInput["raids"][number]["bosses"][number];
  index: number;
}) {
  return (
    <div className="h-full w-full rounded-md border bg-background p-2 shadow-xl">
      <div className="min-h-9 rounded-md border bg-background px-3 py-1.5 text-sm">
        {boss.name || `Boss ${index + 1}`}
      </div>
    </div>
  );
}
